import { motion } from "framer-motion";
import type { IndustryChallenge } from "@/data/industriesData";
import { useLanguage } from "@/hooks/useLanguage";
import { navT } from "@/i18n/nav";

interface Props {
  challenges: IndustryChallenge[];
  color?: string;
  /** Question-shaped H2 override (GEO) */
  sectionTitle?: string;
}

const IndustryChallenges = ({ challenges, color, sectionTitle }: Props) => {
  const lang = useLanguage();
  if (challenges.length === 0) return null;

  const accent = color || "var(--primary)";

  return (
    <section className="py-16 sm:py-24">
      <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <span className="w-6 h-0.5 rounded-full bg-primary" />
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{navT("Industry context", lang)}</p>
        </div>
        <div className="mb-10">
          <h2 className="text-xl sm:text-2xl font-bold text-foreground mb-2">{sectionTitle ?? navT("Industry challenges", lang)}</h2>
          <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
            {navT(
              "These are the realities shaping your industry — and exactly where our specialists make a difference.",
              lang,
            )}
          </p>
        </div>

        {/* Static grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {challenges.map((ch, i) => {
            const Icon = ch.icon;
            return (
              <motion.div
                key={i}
                className="p-5 rounded-xl border border-border/50 bg-card/50"
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                <Icon size={28} className="mb-3 shrink-0 text-primary" strokeWidth={1.4} />
                <h3 className="text-base font-semibold text-foreground mb-1.5">{ch.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{ch.description}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default IndustryChallenges;
